import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, CalendarDays, ArrowRight, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const blogJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Blog WING Academy Brasil',
  url: 'https://wingacademybrasil.com.br/blog',
  description: 'Tutoriais, guias e dicas sobre a Console Behringer WING em português.',
  inLanguage: 'pt-BR',
  publisher: { '@type': 'Organization', name: 'WING Academy Brasil' },
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/blog/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title="Blog — Tutoriais e Guias da Console WING"
        description="Tutoriais, guias passo a passo e dicas sobre a Console Behringer WING em português: roteamento, configuração, firmware, SOF, SNIPs e muito mais."
        path="/blog"
        breadcrumbs={[{ name: 'Blog', path: '/blog' }]}
        jsonLd={[blogJsonLd]}
      />

      {/* Header simples */}
      <div className="border-b border-gray-800 bg-gray-950">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" data-testid="blog-back-home" className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Início
          </Link>
          <Link to="/quiz" className="text-yellow-400 hover:text-yellow-300 text-sm font-medium">
            🎯 Fazer Quiz
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-14 pb-10">
        <span className="inline-flex items-center gap-2 bg-yellow-500/15 border border-yellow-500/40 text-yellow-400 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
          <BookOpen className="w-3.5 h-3.5" /> Conteúdo Gratuito
        </span>
        <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">
          Blog <span className="text-yellow-400">WING Academy</span>
        </h1>
        <p className="text-gray-400 text-base md:text-lg max-w-2xl">
          Tutoriais, guias passo a passo e dicas práticas sobre a Console Behringer WING — em português, por quem opera profissionalmente.
        </p>
      </div>

      {/* Lista de posts */}
      <div className="max-w-5xl mx-auto px-4 pb-20" data-testid="blog-posts-list">
        {loading ? (
          <p className="text-gray-400 py-12 text-center">Carregando artigos...</p>
        ) : posts.length === 0 ? (
          <p className="text-gray-400 py-12 text-center">Nenhum artigo publicado ainda. Volte em breve!</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group block bg-gray-900 border border-gray-800 hover:border-yellow-500/50 rounded-xl p-6 transition-colors"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-yellow-500/15 text-yellow-400 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                    {post.categoria}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                    <Clock className="w-3.5 h-3.5" /> {post.tempo_leitura} min
                  </span>
                </div>
                <h2 className="text-lg md:text-xl font-bold text-white group-hover:text-yellow-400 transition-colors mb-2">
                  {post.titulo}
                </h2>
                <p className="text-gray-400 text-sm mb-4 line-clamp-3">{post.resumo}</p>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {new Date(post.data_criacao).toLocaleDateString('pt-BR')}
                  </span>
                  <span className="inline-flex items-center gap-1 text-yellow-400 text-sm font-medium">
                    Ler artigo <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
